"use client"

import { useState } from 'react'
import { Skeleton } from '@/components/ui/skeleton'
import { useEvaluationAnalysis, type EvaluationAnalysisPayload } from '@/hooks/use-evaluation-analysis'
import { EvaluationDimensionRow } from '@/components/evaluation/evaluation-dimension-row'
import { FindingList } from '@/components/evaluation/finding-list'
import { groupCriteriaResults, isV2AnalysisPayload, filterScoredCriteria } from '@/components/evaluation/evaluation-criteria-section'

export type EvaluationAnalysisInlineProps = {
  evaluationId: string
  analysisHeadline?: string | null
  className?: string
  prefetchedPayload?: EvaluationAnalysisPayload | null
}

export function EvaluationAnalysisInline({
  evaluationId,
  analysisHeadline,
  className,
  prefetchedPayload,
}: EvaluationAnalysisInlineProps) {
  const [isOpen, setIsOpen] = useState(false)
  const {
    payload: fetchedPayload,
    isLoading,
    isMissing,
  } = useEvaluationAnalysis(evaluationId, {
    enabled: isOpen && !prefetchedPayload,
  })

  const payload = prefetchedPayload ?? fetchedPayload

  const headline = analysisHeadline || payload?.headline
  const showLoading = isLoading && !prefetchedPayload

  if (!evaluationId) {
    return null
  }

  return (
    <details
      className={className}
      open={isOpen}
      onToggle={(event) => setIsOpen((event.target as HTMLDetailsElement).open)}
    >
      <summary className="cursor-pointer list-none rounded-md border border-border/60 bg-muted/30 px-3 py-2 text-sm [&::-webkit-details-marker]:hidden [&::marker]:hidden">
        <span className="font-medium text-foreground">{headline || 'View analysis'}</span>
        <span className="ml-2 text-muted-foreground">{isOpen ? 'Hide details' : 'Show details'}</span>
      </summary>
      <div className="space-y-3 border border-t-0 border-border/60 px-3 py-3">
        {showLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-5/6" />
            <Skeleton className="h-4 w-2/3" />
          </div>
        ) : isMissing ? (
          <p className="text-sm text-muted-foreground">Analysis is not available yet.</p>
        ) : payload ? (
          <>
            {isV2AnalysisPayload(payload) ? (
              <p className="text-xs text-muted-foreground">
                {groupCriteriaResults(payload.criteria_results).focusAreas.length} focus area
                {groupCriteriaResults(payload.criteria_results).focusAreas.length === 1 ? '' : 's'}
              </p>
            ) : null}
            <div className="space-y-3">
              {filterScoredCriteria(
                isV2AnalysisPayload(payload) && groupCriteriaResults(payload.criteria_results).dimensions.length > 0
                  ? groupCriteriaResults(payload.criteria_results).dimensions
                  : payload.dimensions,
              )
                .slice(0, 2)
                .map((dimension) => (
                  <EvaluationDimensionRow
                    key={dimension.key}
                    label={dimension.label}
                    score={dimension.score as number}
                    reasoning={dimension.reasoning}
                  />
                ))}
            </div>
            {payload.findings.highlights.length > 0 ? (
              <div className="space-y-1">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Highlights
                </p>
                <FindingList items={payload.findings.highlights} tone="success" max={3} />
              </div>
            ) : null}
            {payload.findings.red_flags.length > 0 ? (
              <div className="space-y-1">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Gaps
                </p>
                <FindingList items={payload.findings.red_flags} tone="warning" max={2} />
              </div>
            ) : null}
          </>
        ) : null}
      </div>
    </details>
  )
}
